import React, { useEffect, useState } from 'react';
import { collection, doc, getCountFromServer, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../auth/useAuth';
import { BILLING_ENABLED, TEAM_TIERS, type BillingInterval, type TeamTierKey } from '../../config/plans';
import { startCheckoutForPrice, openBillingPortal } from '../../services/billing';
import { TeamPricing } from './TeamPricing';

type OrgConfig = {
  orgPlan?: string;
  orgStatus?: string;
  seatLimit?: number;
  orgBillingInterval?: string;
  orgCurrentPeriodEnd?: { toDate?: () => Date };
  orgCancelAtPeriodEnd?: boolean;
};

/**
 * Team/B2B billing for the admin panel. Lets an org owner subscribe to a team
 * plan, see seat usage, and manage their subscription. Hidden while billing is
 * dormant. Org entitlement is read from the admin's own adminConfigs doc (which
 * the webhook writes); seat usage counts the admin's invited users.
 */
export const TeamBilling: React.FC = () => {
  const { profile } = useAuth();
  const [org, setOrg] = useState<OrgConfig | null>(null);
  const [seatsUsed, setSeatsUsed] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uid = profile?.uid;

  useEffect(() => {
    if (!BILLING_ENABLED || !uid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const cfgSnap = await getDoc(doc(db, 'adminConfigs', uid));
        const seatAgg = await getCountFromServer(
          query(collection(db, 'users'), where('invitedBy', '==', uid)),
        );
        if (cancelled) return;
        setOrg(cfgSnap.exists() ? (cfgSnap.data() as OrgConfig) : {});
        setSeatsUsed(seatAgg.data().count);
      } catch {
        if (!cancelled) setError('Could not load team billing.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  if (!BILLING_ENABLED) return null;

  const active = org?.orgPlan === 'pro';
  const seatLimit = org?.seatLimit ?? 0;
  const activeTier: TeamTierKey | null =
    TEAM_TIERS.find((t) => t.seatLimit === seatLimit)?.key ?? null;
  const periodEnd = org?.orgCurrentPeriodEnd?.toDate?.();

  const handleSelect = async (priceId: string, interval: BillingInterval) => {
    setBusy(true);
    setError(null);
    try {
      await startCheckoutForPrice(priceId, interval); // redirects away
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not start checkout.');
    }
  };

  const handleManage = async () => {
    setBusy(true);
    setError(null);
    try {
      await openBillingPortal();
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not open billing portal.');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-white">Team plan</h3>
        <p className="text-sm text-gray-400">
          Subscribe to give every player you invite full Pro access under your branding.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : active ? (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-white">
              {TEAM_TIERS.find((t) => t.key === activeTier)?.label ?? 'Team'} plan
            </div>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300">
              ACTIVE
            </span>
          </div>
          <div className="text-sm text-gray-300">
            Seats: <span className="font-semibold text-white">{seatsUsed ?? '—'}</span> / {seatLimit}
          </div>
          {periodEnd && (
            <div className="text-xs text-gray-400">
              {org?.orgCancelAtPeriodEnd
                ? `Access ends ${periodEnd.toLocaleDateString()}`
                : `Renews ${periodEnd.toLocaleDateString()}`}
              {org?.orgBillingInterval
                ? ` · billed ${org.orgBillingInterval === 'year' ? 'annually' : 'monthly'}`
                : ''}
            </div>
          )}
          <button
            onClick={handleManage}
            disabled={busy}
            className="w-full mt-2 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {busy ? 'Opening…' : 'Manage / change plan'}
          </button>
        </div>
      ) : (
        <TeamPricing onSelect={handleSelect} busy={busy} activeTier={activeTier} />
      )}

      {error && <div className="text-sm text-red-400">{error}</div>}
    </div>
  );
};
